<template>
  <div class="production-summary">
    <Spinner v-if="loading" />
    <table width="100%" border="0" v-if="!loading">
      <thead>
        <tr>
          <th :width="titleColumnWidth"> </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip label="Order Started">
              <i class="fas fa-play-circle"></i>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell" v-for="stage in productionStagesUsed" :key="stage.id">
            <Tooltip :label="stage.name">
              <div class="production-stage-avatar" :style="stageAvatarStyles(stage)">{{stage.avatarName}}</div>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip label="Delivery" v-if="isDespatchDelivery">
              <i class="fas fa-truck"></i>
            </Tooltip>
            <Tooltip label="Pickup" v-if="isDespatchPickup">
              <i class="fas fa-person-carry"></i>
            </Tooltip>
          </th>
          <th width="10%" class="text-center production-cell production-stage-avatar-cell">
            <Tooltip :label="`Completed and ${isDespatchDelivery ? 'Delivered' : 'Picked Up'}`">
              <i class="far fa-check-circle"></i>
            </Tooltip>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr class="production-line border-bottom">
          <td> </td>
          <td class="text-center production-cell">
            <div class="production-tally">Order Started<br />{{order.dateOrderCreated | formatDateCustom('Do MMM h:mma')}}</div>
          </td>
          <td class="text-center production-cell" v-for="stage in productionStagesUsed" :key="stage.id">            
          </td>
          <td class="text-center production-cell">
            
          </td>
          <td class="text-center production-cell">
            <div class="production-tally">Order Estimated Completion<br /> {{order.dateCurrentlyDue | formatDateCustom('Do MMM h:mma')}}</div>
          </td>
        </tr>
        <template v-for="(item, itemIndex) in orderItemDetailList">
          <tr v-if="item.showTitle && itemIndex > 0" class="production-overview-spacer" :key="`order-item-${item.id}-title-spacer`">
            <td :colspan="productionStagesUsed.length + 4"> </td>
          </tr>
          <tr v-if="item.showTitle" :key="`order-item-${item.id}-title`">
            <td>
              <h4>{{item.title}}</h4>
            </td>
          </tr>

          <!-- Order Item Row -->
          <tr :key="`order-item-${item.id}`" :class="{'production-line': true, 'production-line-cancelled': isOrderItemCancelled(item)}" >
            <td class="font-weight-bold">
              <template v-if="item.isProcessedItem || isOrderItemTemplate(item)">
                <ExpandCollapseButton :expanded="isOrderItemExpanded(item)" @update="setOrderItemExpanded(item, $event)" class="mr-2" />
                <a @click.prevent="toggleOrderItemDisplay(item)" :class="{'production-item-cancelled': item.isCancelled}">{{item.quantity}}x {{item.stock.name}} - {{item.glassHeight}}x{{item.glassWidth}}</a>
              </template>
              <template v-if="!item.isProcessedItem && !isOrderItemTemplate(item)">
                <ExpandCollapseButton :disabled="true" class="mr-2" />
                <span :class="{'production-item-stock': true, 'production-item-cancelled': item.isCancelled}">{{item.quantity}}x {{item.stock.name}} - {{item.glassHeight}}x{{item.glassWidth}}</span>
              </template>
              <div class="ml-4" v-if="orderItemRecutCount(item) > 0">
                <LabelBadge labelClass="label-order-type-nonChargeableRework">{{orderItemRecutCount(item)}}x Recut</LabelBadge>
              </div>
            </td>

            <!-- Processed Items: Empty Stages -->
            <td colspan="2" width="10%" class="text-center production-cell" v-if="isOrderItemWaitingPanels(item)">
              Pending Panel Creation
            </td>

            <template v-if="isOrderItemGeneral(item) || isOrderItemGlassStock(item) || item.panelList.length > 0">
              <td width="10%" class="text-center production-cell" v-for="(segment, segmentIndex) in item.segments" :key="segmentIndex">
                <div :class="{
                  'production-item': segmentIndex > 0,
                  'production-item-skip': !segment.hasDot,
                  'production-item-progress': segment.prevActive && !segment.prevCompleted,
                  'production-item-complete': segment.prevCompleted }">
                  <div :class="{
                    'production-dot': segment.hasDot,
                    'production-dot-clickable': segment.hasDot,
                    'production-dot-waiting': segment.hasDot && segment.waiting,
                    'production-dot-progress': segment.hasDot && segment.active && !segment.completed,
                    'production-dot-complete': segment.hasDot && segment.completed
                    }" @click.prevent="displayOrderItemPopover(item, $event)"></div>
                  <div class="production-tally production-tally-offset" v-if="segment.totalCount > 0 && segment.completedCount > 0 && segment.completedCount < segment.totalCount">{{segment.completedCount}} / {{segment.totalCount}}</div>
                </div>
              </td>
            </template>

          </tr>

          <!-- Panel -->
          <template v-if="isOrderItemExpanded(item)">
            <template v-for="panel in item.panelList">
              <tr :key="`order-item-${item.id}-panel${panel.id}`" :class="['production-line', {'production-line-recut': isPanelRecutStopped(panel), 'production-line-cancelled': isPanelCancelled(panel)}]">
                <td class="panel-title-cell">
                  <i :class="['fas', 'fa-sm', 'fa-caret-right', 'mr-2', 'text-muted', 'panel-title']"></i>
                  <a :class="{'production-panel-recut-stopped': isPanelRecutStopped(panel), 'production-panel-recut': panel.isRecut, 'production-panel-cancelled': isPanelCancelled(panel), 'production-panel-completed': isPanelCompleted(panel)}" @click.prevent="displayPanelPopover(item, panel, null, $event)">Panel #{{(panel.panelIndex + 1)}} - {{panel.barcode}}</a>
                  <div class="ml-4" v-if="panel.isRecut || isPanelCancelled(panel) || isPanelRecutStopped(panel)">
                    <LabelBadge labelClass="label-production-status-recut" v-if="isPanelRecutStopped(panel)">Stopped: Recut</LabelBadge>
                    <LabelBadge labelClass="label-order-status-denied" v-if="isPanelCancelled(panel)">Stopped: Cancelled</LabelBadge>
                    <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isRecut">Recut</LabelBadge>
                    
                  </div>
                </td>

                <td width="10%" class="text-center production-cell" v-for="(segment, segmentIndex) in panel.segments" :key="segmentIndex">
                  <div :class="{
                    'production-item': segmentIndex > 0,
                    'production-item-skip': !segment.hasDot,
                    'production-item-progress': segment.prevActive && !segment.prevCompleted,
                    'production-item-complete': segment.prevCompleted }">
                    <div :class="{
                      'production-dot': segment.hasDot,
                      'production-dot-clickable': segment.hasDot,
                      'production-dot-waiting': segment.hasDot && segment.waiting,
                      'production-dot-progress': segment.hasDot && segment.active && !segment.completed,
                      'production-dot-complete': segment.hasDot && segment.completed
                      }" @click.prevent="displayPanelPopover(item, panel, segment, $event)"></div>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Spacers -->
            <tr class="production-line-spacer" :key="`order-item-${item.id}-spacer`" v-if="itemIndex < orderItemDetailList.length-1">
              <td> </td>
            </tr>
            <tr class="production-line-spacer" v-show="!compactMode && itemIndex < orderItemDetailList.length-1" :key="`order-item-${item.id}-spacer-extra`">
              <td> </td>
            </tr>

          </template>
        </template>

        <tr class="production-overview-spacer">
          <td :colspan="productionStagesUsed.length + 4"> </td>
        </tr>
      </tbody>
    </table>
    <OrderProductionPanelPopover ref="panelPopover" :showButtons="showPopoverButtons" />
    <OrderProductionPanelStageTaskPopover ref="panelStageTaskPopover" />
    <OrderProductionPanelDespatchPopover ref="panelDespatchPopover" />
    <OrderProductionOrderItemPopover ref="orderItemPopover" />
  </div>
</template>

<style lang="scss" scoped>
$completed-colour: #3EB240;
$cancelled-colour: $label-status-cancelled;
$waiting-colour: #487eb0;
$progress-colour: #FF8D3B;
$dot-size: 15px;
$stage-avatar-size: 30px;

.production-summary {
  font-size: 13px;
  color: #676a6c;
}
.theme-dark .production-summary {
  color: #c7c7c7;
}
.production-line {
  height: 40px;
}
.production-line-spacer {
  height: 15px;
}
.production-overview-spacer {
  height: 20px;
}

.production-summary td {
  padding-top: 0.4rem;
}
.production-summary th {
}
.production-summary th i {
  font-size: 18px;
  color: #5d666e;
}
.theme-dark .production-summary th i {
  color: #a0aab3;
}
.production-cell {
  position: relative;
  height: 40px;
}
.production-dot {
  display: inline-block;
  margin-top: 0.4rem;
  width: $dot-size;
  height: $dot-size;
  border-radius: 50%;
  border: 2px solid #757997;
}
.production-dot-clickable {
  cursor: pointer;
}
.production-dot-clickable:hover {
  box-shadow: 0 0 0 2px #d08080;
}
.production-dot-waiting {
  border: 3px solid $progress-colour;
}
.production-dot-complete {
  border: 4px solid $completed-colour;
  background-color: $completed-colour;
}
.production-line-recut .production-dot-complete,
.production-line-cancelled .production-dot-complete {
  border-color: $cancelled-colour;
  background-color: $cancelled-colour;
}

.production-icon-cancelled {
  color: $cancelled-colour;
}

.production-dot-progress {
  border: 3px solid $progress-colour;
  background-color: $progress-colour;
}
.production-item {
  position: relative;
  width: 100%;
  height: 26px;

  &:before,
  &:after {
    content: "";
    height: 4px;
    /*background-color: #9295af;*/
    background-color: #c4c6d8;
    position: absolute;
    z-index: 0;
    width: calc(100% - 24px);
    left: calc(-50% + 10px);
    top: 50%;
    transform: translateY(-50%);
    transition: all .25s ease-out;
  }
}
.production-item-skip::after {
  width: 100%;
  left: 0;
}
.production-item-complete::after {
  background-color: $completed-colour;
}
.production-line-recut .production-item-complete::after,
.production-line-cancelled .production-item-complete::after,
.production-line-cancelled .production-item-complete::before,
.production-line-cancelled .production-item-skip.production-item-complete::before {
  background-color: $cancelled-colour;
}
.production-item-progress::after {
  background-color: #FF8D3B;
}
.production-item-skip.production-item-complete::before {
  background-color: $completed-colour;
}
.production-item-skip.production-item-progress::before {
  background-color: #FF8D3B;
}
.production-tally {
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  width: 100%;
  color: #757997;
}
.theme-dark .production-tally {
  color: #9fa3be;
}

.production-tally-offset {
  position: absolute;
  margin: 0 auto;
}
.production-note {
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  color: #757997;
}
.theme-dark .production-note {
  color: #9fa3be;
}

/* Order Item */
.production-item-cancelled {
  text-decoration: line-through !important;
  color: $label-status-cancelled !important;
}

/* Panel */
.production-panel-recut-stopped, .production-panel-cancelled {
  text-decoration: line-through !important;
  color: $label-status-cancelled !important;
}
.production-panel-completed {
  color: $status-completed !important;
}
.panel-title {
  margin-left: 1.1rem;
}
td.panel-title-cell > a:hover {
  text-decoration:underline !important;
}
td.panel-title-cell .label,
tr.production-line .label {
  margin-left: 0.5rem;
  font-size: 0.6rem !important;
  padding: 1px 5px 1px 5px !important;
}

/* Stage Avatar */
.production-stage-avatar-cell {
}

.production-stage-avatar {
  margin-left: calc(50% - #{$stage-avatar-size} / 2);
  font-weight: bold;
  width: $stage-avatar-size;
  height: $stage-avatar-size;
  font-size: 0.9rem;
  line-height: $stage-avatar-size;
  vertical-align: middle;
  text-align: center;
  border-radius: 50%;
}
</style>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import colourMixin from '@/mixins/colour'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'

import OrderProductionPanelPopover from '@/modules/orders/components/orderProductionPanelPopover'
import OrderProductionPanelStageTaskPopover from '@/modules/orders/components/orderProductionPanelStageTaskPopover'
import OrderProductionPanelDespatchPopover from '@/modules/orders/components/orderProductionPanelDespatchPopover'
import OrderProductionOrderItemPopover from '@/modules/orders/components/orderProductionOrderItemPopover'

import { InventoryStockTypes, DespatchTypes, ProductionPanelStatusTypes, ProductionPanelStageTaskStatusTypes, TaskStatusTypes, TaskItemStatusTypes } from '@/store/types'

export default {
  name: 'OrderProductionPreview',
  mixins: [colourMixin, orderItemMixin],
  components: {
    OrderProductionPanelPopover,
    OrderProductionPanelStageTaskPopover,
    OrderProductionPanelDespatchPopover,
    OrderProductionOrderItemPopover
  },
  props: {
    order: Object,
    compactMode: {
      type: Boolean,
      default: true
    },
    showPopoverButtons: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      productionPanels: [],
      productionStagesUsed: [],
      taskList: [],
      orderItemSettings: {},

      orderItemDetailList: [],
      hasProcessedOrderItems: false,
      hasStockOrderItems: false
    }
  },
  watch: {
    'order.id': function(newVal, oldVal) {
      // Change subscription
      this.unsubscribeEndpoint(`/orders/${oldVal}/production`)
      this.unsubscribeEndpoint(`/orders/${oldVal}`)

      this.subscribeEndpoint(`/orders/${this.order.id}/production`, () => {
        this.requestPanelData()
      })
      this.subscribeEndpoint(`/orders/${this.order.id}`, () => {
        this.requestPanelData()
      })

      this.requestPanelData()
    }
  },
  created() {
    this.DespatchTypes = DespatchTypes
  },
  mounted() {
    this.requestPanelData()

    this.subscribeEndpoint(`/orders/${this.order.id}/production`, () => {
      this.requestPanelData()
    })
    this.subscribeEndpoint(`/orders/${this.order.id}`, () => {
      this.requestPanelData()
    })
    this.subscribeEndpoint(`/orders/production`, (data) => {
      // Check if this order is in the list
      if(_.isArray(data.orderIdList)) {
        if(data.orderIdList.indexOf(this.order.id) != -1)
          this.requestPanelData()
      }
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/orders/${this.order.id}/production`)
    this.unsubscribeEndpoint(`/orders/${this.order.id}`)
    this.unsubscribeEndpoint(`/orders/production`)
  },
  computed: {
    isDespatchDelivery() {
      return this.order.despatchType == DespatchTypes.delivery
    },
    isDespatchPickup() {
      return this.order.despatchType == DespatchTypes.pickup
    },
    titleColumnWidth() {
      // Stage/Despatch columns are fixed width. We adjust the title column to take up the remaining space
      var stageColumnWidth = 10
      var startedColumnWidth = stageColumnWidth
      var despatchColumnWidth = stageColumnWidth
      var completedColumnWidth = stageColumnWidth
      let stageCount = this.productionStagesUsed.length

      return (100 - (stageCount * stageColumnWidth + despatchColumnWidth + startedColumnWidth + completedColumnWidth)) + '%'
    }
  },
  methods: {
    requestPanelData() {
      this.loadingError = ''

      var requestList = [
        this.$apiService.ordersService.getOrderProductionPanelList(this.order.id)
      ]
      if(this.userHasReadPermission(this.PermissionTypes.TaskPermissionTypes.task))
        requestList.push(this.$apiService.tasksService.getTaskList({ filter: { orderId: this.order.id } }))
      
      Promise.all(requestList)
      .then(results => {
        this.productionPanels = results[0].data
        this.taskList = results.length > 1 ? results[1].data : []

        // Calculate the production stages used
        this.productionStagesUsed = []
        this.productionPanels.forEach(panel => {

          panel.productionPanelStageTasks.sort((task1, task2) => task1.taskOrder - task2.taskOrder)

          panel.productionPanelStageTasks.forEach(panelTask => {
            if(panelTask.productionStage) {
              var hasStage = this.productionStagesUsed.find(stage => stage.id == panelTask.productionStage.id)
              if(!hasStage) {
                var stage = _.clone(panelTask.productionStage)

                // Calculate some fields for the stage
                stage.avatarName = ''
                if(!stage.logoImageURL)
                  stage.avatarName = Utils.calculateAvatarInitials(stage.name)

                this.productionStagesUsed.push(stage)
              }
            }
          })
        })

        this.productionStagesUsed.sort((stage1, stage2) => stage1.sortOrder - stage2.sortOrder)

        // Pre-calculate the details
        this.calculatePreviewDetails()
      })
      .catch(err => {
        console.error(err)
        this.loadingError = 'Unable to load order production status'
      })
      .finally(() => {
        this.loading = false
      })
    },
    calculatePreviewDetails() {
      this.hasProcessedOrderItems = false
      this.hasStockOrderItems = false

      if(!this.order.orderItems)
        return

      var orderItemDetailList = _.cloneDeep(this.order.orderItems.filter(item => {
        if(item.stockType == InventoryStockTypes.glass) {      
          // Always show root glass items
          if(item.parentOrderItemId == null)
            return true

          // If they are part of a group (eg, glass unit) then only show them if there are panels linked to this glass item
          let linkedPanels = this.productionPanels.filter(panel => panel.orderItemId == item.id)
          if(linkedPanels.length > 0)
            return true
        }

        // Always show template items
        if(item.stockType == InventoryStockTypes.template)
          return true
        
        return false
      }))

      // Calculate panels per order item & all the stats
      orderItemDetailList.forEach(orderItem => {
        orderItem.panelList = []
        orderItem.stages = []
        orderItem.segments = []

        let orderItemTaskItems = this.findActiveTaskItemsForOrderItem(orderItem)

        orderItem.panelList = _.cloneDeep(this.productionPanels.filter(panel => panel.orderItemId == orderItem.id))
        orderItem.validPanelList = orderItem.panelList.filter(panel => {
          return panel.status != ProductionPanelStatusTypes.recut && panel.status != ProductionPanelStatusTypes.cancelled
        })

        // Display Info
        orderItem.showTitle = true
        if(orderItem.stockType == InventoryStockTypes.glass) {
          orderItem.title = 'Processed Glass'
          orderItem.sortOrder = 0
          if(!orderItem.isProcessedItem) {
            orderItem.title = 'Glass Stock'
            orderItem.sortOrder = 2
          }
        }
        else if(orderItem.stockType == InventoryStockTypes.template) {
          orderItem.title = 'Templates'
          orderItem.sortOrder = 1
        }
        else {
          orderItem.title = 'Other'
          orderItem.sortOrder = 3
        }

        orderItem.panelList.forEach(panel => {
          panel.segments = [{
            id: null,
            name: 'Start',
            isStart: true,
            hasDot: true,
            waiting: false,
            active: false,
            completed: true,
            cancelled: orderItem.isCancelled
          }]
          this.productionStagesUsed.map(stage => {
            let panelTaskIndex = panel.productionPanelStageTasks.findIndex(panelTask => panelTask.productionStage.id == stage.id)
            var panelStageInfo = this.createSegment({ id: stage.id, name: stage.name, isStage: true, cancelled: orderItem.isCancelled })

            if(panelTaskIndex != -1) {
              var panelTask = panel.productionPanelStageTasks[panelTaskIndex]

              panelStageInfo.hasDot = true
              panelStageInfo.active = panelTask.status == ProductionPanelStageTaskStatusTypes.production
              panelStageInfo.completed = panelTask.status == ProductionPanelStageTaskStatusTypes.completed

              if(panelTaskIndex > 0) {
                let prevStageTask = panel.productionPanelStageTasks[panelTaskIndex-1]
                if(prevStageTask.status == ProductionPanelStageTaskStatusTypes.completed && panelTask.status == ProductionPanelStageTaskStatusTypes.pending) {
                  panelStageInfo.waiting = true
                }
              } else if(panelTaskIndex == 0) {
                // First task is always waiting
                panelStageInfo.waiting = true
              }
            }

            panel.segments.push(panelStageInfo)
          })
          panel.segments.push(this.createSegment({
            name: 'Despatch',
            isDespatch: true,
            hasDot: true,
            waiting: panel.status == ProductionPanelStatusTypes.despatch,
            active: orderItemTaskItems.length > 0,
            completed: panel.status == ProductionPanelStatusTypes.completed,
            cancelled: orderItem.isCancelled
          }))
          panel.segments.push(this.createSegment({
            name: 'Completed',
            isCompleted: true,
            hasDot: true,
            completed: panel.status == ProductionPanelStatusTypes.completed,
            cancelled: orderItem.isCancelled
          }))

          // Calculating skipping segments
          this.finishSegmentList(panel.segments)
        })

        // Build order item segment summary based on panels
        if(orderItem.panelList.length > 0)
          orderItem.segments = _.cloneDeep(orderItem.panelList[0].segments)

        orderItem.segments.forEach((orderItemSegment, segmentIndex) => {
          orderItemSegment.isOrderItem = true
          orderItemSegment.completedCount = 0
          orderItemSegment.totalCount = orderItem.validPanelList.length
          var activeCount = 0
          var waitingCount = 0

          orderItem.validPanelList.forEach(panel => {
            let panelSegment = panel.segments[segmentIndex]

            if(panelSegment.completed)
              orderItemSegment.completedCount++
            if(panelSegment.active)
              activeCount++
            if(panelSegment.waiting)
              waitingCount++
          })

          orderItemSegment.completed = orderItemSegment.completedCount == orderItemSegment.totalCount && orderItemSegment.completedCount > 0
          orderItemSegment.active = (activeCount + orderItemSegment.completedCount) > 0 && !orderItemSegment.completed
          orderItemSegment.waiting = waitingCount > 0 && !orderItemSegment.active && !orderItemSegment.completed
        })

        // Build segments for non-processed items
        if((orderItem.stockType == InventoryStockTypes.glass && !orderItem.isProcessedItem) || orderItem.stockType == InventoryStockTypes.general) {
          orderItem.segments = [{
            id: null,
            name: 'Start',
            isStart: true,
            hasDot: true,
            waiting: false,
            active: false,
            completed: true,
            cancelled: orderItem.isCancelled
          }]
          this.productionStagesUsed.map(stage => {
            var orderItemStageInfo = this.createSegment({ isStage: true, cancelled: orderItem.isCancelled })

            orderItem.segments.push(orderItemStageInfo)
          })
          orderItem.segments.push(this.createSegment({
            name: 'Despatch',
            isDespatch: true,
            hasDot: true,
            waiting: orderItem.despatchedQuantity < orderItem.quantity,
            active: orderItemTaskItems.length > 0,
            completed: orderItem.despatchedQuantity >= orderItem.quantity,
            cancelled: orderItem.isCancelled
          }))
          orderItem.segments.push(this.createSegment({
            name: 'Completed',
            isCompleted: true,
            hasDot: true,
            completed: orderItem.despatchedQuantity >= orderItem.quantity,
            cancelled: orderItem.isCancelled
          }))
        }

        this.finishSegmentList(orderItem.segments)        
      })

      // Group the processed / non processed / templates / other
      // Process titles
      orderItemDetailList.sort((itemA, itemB) => {
        if(itemA.sortOrder == itemB.sortOrder)
          return itemA.id - itemB.id
        return itemA.sortOrder - itemB.sortOrder
      })
      var curTitle = ''
      orderItemDetailList.forEach(orderItem => {
        if(orderItem.title == curTitle)
          orderItem.showTitle = false
        else
          curTitle = orderItem.title
      })

      this.orderItemDetailList = _.cloneDeep(orderItemDetailList)
    },

    isPanelRecutStopped(panel) {
      return panel.status == ProductionPanelStatusTypes.recut
    },
    isPanelCancelled(panel) {
      return panel.status == ProductionPanelStatusTypes.cancelled
    },
    isPanelCompleted(panel) {
      return panel.status == ProductionPanelStatusTypes.completed
    },
    stageAvatarStyles(stage) {
      var lightenAmount = 25      // Lighten the stage colour a bit to better suit the order page
      var bgColour = this.lightenColour(stage.highlightColour, lightenAmount)

      if(stage.logoImageURL)
        return { 'background-image': `url('${stage.logoImageURL}')`, 'background-size': 'contain' }

      return {
        'background-color': bgColour,
        'color': this.textColourForBackground(bgColour)
      }
    },

    orderItemPanelList(orderItem) {
      return this.productionPanels.filter(panel => panel.orderItemId == orderItem.id)
    },
    findActiveTaskItemsForOrderItem(orderItem) {
      var taskItems = []
      this.taskList.filter(task => {

        if(task.taskItems) {
          taskItems = _.concat(taskItems, task.taskItems.filter(taskItem => {
            if(taskItem.status == TaskItemStatusTypes.completed || taskItem.status == TaskItemStatusTypes.cancelled)
              return false

            if(taskItem.orderItems) {
              return taskItem.orderItems.find(listOrderItem => listOrderItem.id == orderItem.id) != null
            }
            return false
          }))
        }
      })

      return taskItems
    },

    displayPanelPopover(orderItem, panel, segment, evt) {
      if(segment && segment.isDespatch)
        this.$refs.panelDespatchPopover.displayFromElement(evt.target, panel, this.order, this.taskList)
      else if(segment && segment.isStage)
        this.$refs.panelStageTaskPopover.displayFromElement(evt.target, panel, segment)
      else
        this.$refs.panelPopover.displayFromElement(evt.target, orderItem, panel)
    },
    displayOrderItemPopover(orderItem, evt) {
      this.$refs.orderItemPopover.displayFromElement(evt.target, this.order, orderItem, this.taskList)
    },

    isOrderItemExpanded(orderItem) {
      if(this.orderItemSettings[orderItem.id] === undefined) {
        // Default to expanded for non-compact mode
        this.$set(this.orderItemSettings, orderItem.id, !this.compactMode)
      }

      return !!this.orderItemSettings[orderItem.id]
    },
    setOrderItemExpanded(orderItem, expanded) {
      this.$set(this.orderItemSettings, orderItem.id, expanded)
    },
    toggleOrderItemDisplay(orderItem) {
      this.$set(this.orderItemSettings, orderItem.id, !(this.orderItemSettings[orderItem.id] || false))
    },
    isOrderItemGlass(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass
    },
    isOrderItemGeneral(orderItem) {
      return orderItem.stockType == InventoryStockTypes.general
    },
    isOrderItemGlassProcessed(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass && orderItem.isProcessedItem
    },
    isOrderItemGlassStock(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass && !orderItem.isProcessedItem
    },
    isOrderItemTemplate(orderItem) {
      return orderItem.stockType == InventoryStockTypes.template
    },
    isOrderItemWaitingPanels(orderItem) {
      return (!orderItem.panelList || orderItem.panelList.length == 0) && (orderItem.isProcessedItem || this.isOrderItemTemplate(orderItem))
    },
    isOrderItemCancelled(orderItem) {
      return orderItem.isCancelled
    },
    orderItemRecutCount(orderItem) {
      return orderItem.panelList.filter(panel => panel.isRecut).length
    },

    // Create a new segment object
    createSegment(values) {
      return _.merge({
        id: null,
        name: '',
        isOrderItem: false,
        isStage: false,
        hasDot: false,
        waiting: false,
        active: false,
        completed: false,
        prevCompleted: false,
        prevActive: false,
        totalCount: 0,
        completedCount: 0
      }, values)
    },

    // Finish setting up the segment list with skip calculations
    finishSegmentList(segments) {    
      segments.forEach((segment, segmentIndex) => {
        if(segmentIndex > 0) {
          var prevSegment = segments[segmentIndex-1]

          segment.prevCompleted = prevSegment.completed
          segment.prevActive = false
          if(!prevSegment.completed) {
            if(prevSegment.active && (segment.waiting || segment.active))
              segment.prevActive = true
            if(prevSegment.hasDot && prevSegment.waiting && segment.active)
              segment.prevActive = true
          }

          // Copy forward status for skip aheads
          if(!segment.hasDot) {
            segment.completed = prevSegment.completed
          }
        }
      })
    }
  }
  
}
</script>